/**
 * readyState-aware DOM-ready and window-load helpers.
 *
 * A bare `addEventListener('DOMContentLoaded' | 'load', fn)` silently never
 * runs `fn` when the event has already passed. The boot bundles are blocking
 * `<script src>` tags at the end of `<body>`, so a registration made at module
 * scope always beats the event; one made inside a dynamic `import()`
 * continuation does not, and `hookActionSpecificSetup` makes one. These helpers
 * consult `document.readyState` instead, so both run — and a bundle can move
 * to `defer` without either breaking.
 *
 * They deliberately do **not** run the callback synchronously, and must not be
 * simplified to. `boot/main/index.js` registers `mountAllComponents` several
 * statements before it calls `i18nSetup()`, so a synchronous callback would
 * mount every React component on the site before i18n is configured.
 *
 * `'interactive'` takes the deferred path rather than waiting for an event
 * that has already fired. The DOM is parsed by then, so the callback is safe
 * to run. What would make the wait necessary is an initialiser that reads
 * layout geometry, since stylesheets are not guaranteed applied until
 * `'complete'`; the action entry points only attach listeners, and every
 * measurement they reach runs inside a handler, on a page long since laid out.
 */

type Callback = () => void;

/**
 * Queue `callback` as a macrotask. The callback is invoked with no arguments.
 */
function defer(callback: Callback): void {
  setTimeout(() => {
    callback();
  }, 0);
}

/**
 * Run `callback` once the DOM is parsed, or on the next macrotask if it
 * already is.
 */
export function onReady(callback: Callback): void {
  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        callback();
      },
      { once: true }
    );
    return;
  }

  defer(callback);
}

/**
 * Run `callback` once the page and its subresources have loaded, or on the
 * next macrotask if they already have.
 */
export function onWindowLoad(callback: Callback): void {
  if (document.readyState === 'complete') {
    defer(callback);
    return;
  }

  window.addEventListener(
    'load',
    () => {
      callback();
    },
    { once: true }
  );
}
